import Cookies from 'js-cookie';
import apiClient from 'services/_infra/apiClient';
import { loginfo } from 'services/_infra/util/apiUsageLogger';
import { tryMsAuthSilent } from './ms.service';

/**
 * Writes the signed-in workplace to the `workplaceId` cookie, or removes it.
 *
 * `src/hooks/useAlarmEvents.js` reads this cookie to tell this browser's own
 * alarms from every other workplace's. It expects a number or nothing. A value
 * it cannot read, such as the string `"undefined"`, is worse than no cookie at
 * all, because the hook would then hold an id that matches no alarm.
 *
 * So anything that is not a finite number REMOVES the cookie. A stale cookie
 * from an earlier session must not outlive a login that could not say.
 *
 * @param {number|string|null|undefined} workplaceId What the server answered.
 */
export function rememberWorkplaceId(workplaceId) {
  // Absence is checked BEFORE the conversion, because `Number(null)` and
  // `Number('')` are both 0 — a finite number, and the wrong answer. The server
  // says `null` when the user has no workplace, and storing "0" for that would
  // leave the alarm hook filtering on a workplace that does not exist, which
  // drops every alarm instead of keeping them all.
  if (workplaceId === null || workplaceId === undefined || workplaceId === '') {
    Cookies.remove('workplaceId');
    return;
  }

  const id = Number(workplaceId);
  if (Number.isFinite(id)) {
    Cookies.set('workplaceId', String(id));
  } else {
    Cookies.remove('workplaceId');
  }
}

export async function getRefreshToken() {
  const res = await apiClient.post('/auth/refresh');
  // A refresh mints a new access token, and the server answers that token's
  // workplace claim. Rewriting the cookie here keeps it alive past its own
  // expiry, and is the only path that corrects it during a long session.
  rememberWorkplaceId(res?.data?.workplaceId);
  return res;
}

export const loginWithCredentials = async (
  email,
  password,
  gdprConsent,
  gdprTermsVersion,
  gdprPolicyVersion,
) => {
  loginfo();

  try {
    const res = await apiClient.post(
      `/auth/login`,
      {
        email: email,
        password: password,
        gdprConsent: gdprConsent,
        gdprTermsVersion: gdprTermsVersion,
        gdprPolicyVersion: gdprPolicyVersion,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    rememberWorkplaceId(res.data.workplaceId);
    return res;
  } catch (error) {
    console.error('Login with credentials failed.', error);
    throw error;
  }
};

export const loginWithProvider = async (
  gdprConsent,
  gdprTermsVersion,
  gdprPolicyVersion,
  provider = null,
  instance = null,
) => {
  loginfo();

  try {
    const msRes = await tryMsAuthSilent(instance, ['User.ReadBasic.All']);

    if (msRes) {
      const res = await apiClient.post(
        `/auth/login`,
        {
          gdprConsent: gdprConsent,
          gdprTermsVersion: gdprTermsVersion,
          gdprPolicyVersion: gdprPolicyVersion,
          provider: provider,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            MsAuth: `Bearer ${msRes.accessToken}`,
          },
          withCredentials: true,
        },
      );

      rememberWorkplaceId(res.data.workplaceId);
      return res;
    } else {
      throw new Error('Failed to authenticate with Microsoft');
    }
  } catch (error) {
    console.error('Login with provider failed.', error);
    throw error;
  }
};

export const createAppSessionFromMsalResult = async (
  msalToken,
  gdprTermsVersion = 1,
  gdprPolicyVersion = 1,
) => {
  const response = await apiClient.post(
    '/auth/login',
    {
      provider: {
        name: 'microsoft-azure',
        ...msalToken,
      },
      gdprConsent: true,
      gdprTermsVersion,
      gdprPolicyVersion,
    },
    {
      headers: {
        MsAuth: `Bearer ${msalToken.accessToken}`,
      },
    },
  );

  rememberWorkplaceId(response.data.workplaceId);
  return response;
};

/**
 * Backoff between logout retries, in ms. Two retries: long enough to ride out
 * an AuthService restart, short enough that nothing is still running by the
 * time the user has landed back on the sign-in screen.
 */
const LOGOUT_RETRY_DELAYS_MS = [400, 1200];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * True when a logout failure means the server could not revoke the session,
 * rather than that there was nothing to revoke.
 *
 * Retrying is only right for the first kind. A 401 means the session is
 * already gone — retrying it would just be noise. A 4xx means the request
 * itself was wrong and will be wrong again next time.
 */
function isRevocationUnavailable(error) {
  const status = error?.response?.status ?? null;
  // No response at all: the request never reached the server (offline, DNS,
  // CORS preflight, connection reset). The session is very likely still live.
  if (status === null) return true;
  if (error?.response?.data?.code === 'REVOKE_UNAVAILABLE') return true;
  return status === 502 || status === 503 || status === 504;
}

/**
 * End the session on the server.
 *
 * Best-effort and NEVER throws. Logout is a one-way door in the UI — by the
 * time this runs the caller is already tearing down local state — so a
 * rejection here has nowhere useful to go, and every caller today fires this
 * without awaiting it. Throwing would only produce an unhandled rejection.
 *
 * Deliberately not awaited by callers: retries must not sit between the user
 * clicking "log out" and the UI clearing. They run alongside the teardown, and
 * touch no component state, so nothing here depends on staying mounted.
 *
 * The retry exists because the failure this guards against is not random — the
 * server can only revoke while the AuthService is reachable, so every restart
 * and deploy is a window where a logout would otherwise silently leave the
 * refresh token live. See Fusion-Backend card 06.
 *
 * @returns {Promise<{ended: boolean, attempts: number, reason?: string}>}
 *   `ended` false means the session may still be live on the server.
 */
export async function logout() {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await apiClient.post('/auth/logout');
      return { ended: true, attempts: attempt + 1 };
    } catch (error) {
      const retryable = isRevocationUnavailable(error);
      const exhausted = attempt >= LOGOUT_RETRY_DELAYS_MS.length;

      if (!retryable || exhausted) {
        // A development aid only. `babel-plugin-transform-remove-console` sits
        // in this repo's ROOT babel plugin list, and babel CONCATENATES the
        // env.test override rather than replacing it, so every console call is
        // stripped from the test and production builds alike. Do not treat this
        // as the record that a session outlived its logout — there is no
        // client-side channel that survives the build. The durable record is
        // the server's audit trail, and the returned outcome is what a caller
        // can actually act on.
        console.warn(
          `[auth] logout did not end the session on the server after ${attempt + 1} attempt(s)` +
            `${retryable ? ' — the AuthService stayed unreachable' : ' — the server rejected the request'}`,
          error,
        );
        return {
          ended: false,
          attempts: attempt + 1,
          reason: retryable ? 'unavailable' : 'rejected',
        };
      }

      await wait(LOGOUT_RETRY_DELAYS_MS[attempt]);
    }
  }
}
